/**
 * THE PANEL FRAME EVERY CHART ON THIS SCREEN SITS IN.
 *
 * It owns the five things that were previously written per panel and drifted:
 *
 *   1. SIZING. `<ResponsiveContainer width="100%" aspect={n}>` inside a wrapper
 *      with a definite `min-height`. Never a percentage height other than 100%,
 *      never a partial percentage width — that combination is what rendered two
 *      charts at zero pixels on every phone and tablet while looking perfect on
 *      a laptop. `dashboard-screen/chart-sizing.test.ts` pins the property.
 *   2. THE SCROLLER. A chart with a real minimum width scrolls inside its own
 *      box, so the PAGE never scrolls sideways.
 *   3. THE FOUR STATES — skeleton, error with a per-panel retry, empty, answer.
 *      A failing panel never blanks its siblings.
 *   4. THE CHART/TABLE TOGGLE, which is the accessibility fallback and is
 *      therefore not optional.
 *   5. PER-PANEL EXPORT — PNG and this panel's own CSV, revealed on hover AND on
 *      keyboard focus.
 *
 * The notes list is attached to the PANEL rather than pooled at the bottom of
 * the page, because a caveat five panels away from its number is a caveat about
 * nothing.
 */
import React, { useCallback, useRef, useState } from 'react';
import { ResponsiveContainer } from 'recharts';
import {
  ChartArea,
  ChartScroller,
  NoteItem,
  NoteList,
  Panel,
  PanelActions,
  PanelBody,
  PanelCaveat,
  PanelControls,
  PanelHead,
  PanelTitle,
  PanelTitleGroup,
  PanelToggle,
  SrOnly,
  Staleable,
} from './chart.styled';
import { ChartEmpty, ChartError, ChartSkeleton, EmptyReason } from './chart-empty';
import { ScrollStrip } from '../../../../scroll/scroll-affordance';
import { StateSwitch } from '../../../../../motion';

/** Which of the four fills the body is showing. Keys the crossfade between
 *  them: skeleton → chart never stacks, and a refetch (stale) keeps the chart
 *  and dims it rather than switching back to the skeleton. The chart/table
 *  toggle is NOT a state here — it swaps instantly, as a control should. */
const bodyState = (
  state: ChartShellState,
  emptyReason: EmptyReason | null | undefined
): 'loading' | 'error' | 'empty' | 'ready' => {
  if (state.loading && !state.stale) return 'loading';
  if (state.error) return 'error';
  if (emptyReason) return 'empty';

  return 'ready';
};

export interface ChartShellState {
  loading: boolean;
  stale?: boolean;
  error?: string;
  onRetry?: () => void;
}

export interface ChartShellProps {
  /** Stable across renders. Keys the first-paint animation, the series toggles
   *  and the E2E selectors. */
  id: string;
  title: string;
  /** Permanent visible copy under the title. NOT a tooltip: several panels carry
   *  a sentence that changes what their number means. */
  caveat?: React.ReactNode;
  /** A second caveat under the first — a substitution, a fallback, a warning
   *  that belongs where the title is rather than only in the notes. */
  extraCaveat?: React.ReactNode;
  wide?: boolean;
  state: ChartShellState;
  emptyReason?: EmptyReason | null;
  /** The server's sentences, plus any the renderer alone knows. Same list,
   *  because they are the same class of statement. */
  notes?: string[];
  /** Header controls, to the left of the Chart/Table toggle. */
  controls?: React.ReactNode;
  /** width/height ratio. 2.4 is the default for a time series; a donut wants
   *  something nearer 1.6. */
  aspect?: number;
  minHeight?: number;
  /** Below this the chart scrolls inside its own box rather than compressing.
   *  24 hourly labels need about 640px. */
  minWidth?: number;
  /** A one-sentence summary for a screen reader. A chart with none is a chart
   *  that does not exist to a screen reader. */
  summary?: string;
  /** The same numbers as rows. Its presence is what offers the toggle. */
  table?: React.ReactNode;
  /** A panel whose figures cannot be drawn (a timestamp per building) forces the
   *  table view and says why, rather than drawing a bar of an instant. */
  forceTable?: boolean;
  /** PNG + CSV for this panel alone. Either may return a promise; the shell
   *  awaits it to keep a second export from starting on top of the first. */
  onExportPng?: (svg: SVGSVGElement | null) => void | Promise<void>;
  onExportCsv?: () => void | Promise<void>;
  /** Rendered under the chart and above the notes. The captions that describe
   *  the SERIES rather than the panel go here — a total, a peak, an anomaly
   *  count — so they sit with the thing they are about instead of becoming tiles
   *  that disagree with the strip at the top of the screen. */
  footer?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * A CHART THAT SCROLLS, AND SAYS SO. Use this, never a bare `ChartScroller`.
 *
 * `ChartScroller` exists so a chart with a real minimum width scrolls inside its
 * own box rather than letting the PAGE scroll sideways — point 2 of this file's
 * header, and deliberate. What was missing was any signal that it had happened.
 * `scripts/analytics/responsive.js` measured it on the Trends tab at every width
 * from a phone to a laptop: **+418px on the hour panel at 320, +298 on dwell,
 * +82 on a 1280 laptop**, with no scrollbar, no peek, no mask and no shade.
 *
 * A REAL SCROLLBAR IS NOT AN OPTION HERE, and it was tried: the overlay bars
 * WebKit and Chromium draw take zero layout space, so `offsetHeight -
 * clientHeight` stays 0 whatever `::-webkit-scrollbar` says — and an overlay bar
 * is invisible until you are already scrolling, which is no use to somebody
 * deciding whether to try. So this is the same `ScrollStrip` the KPI carousel,
 * the chip rows and the tab strip use: one affordance in the product rather than
 * a fourth invention.
 *
 * IT IS A COMPONENT RATHER THAN A STYLE because the shade has to know the
 * scroller's edges, which needs a ref and a hook. That is also the point: three
 * panels — dwell, rhythm and turnover — imported `ChartScroller` DIRECTLY and so
 * never got the affordance the shell added. A styled div cannot stop that; this
 * can.
 */
export const ScrollableChart: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);

  return (
    <ScrollStrip scroller={scrollerRef}>
      <ChartScroller ref={scrollerRef}>{children}</ChartScroller>
    </ScrollStrip>
  );
};

export const ChartShell: React.FC<ChartShellProps> = ({
  id,
  title,
  caveat,
  extraCaveat,
  wide,
  state,
  emptyReason,
  notes,
  controls,
  aspect = 2.4,
  minHeight = 240,
  minWidth,
  summary,
  table,
  forceTable,
  onExportPng,
  onExportCsv,
  footer,
  children,
}) => {
  const [showTable, setShowTable] = useState(false);
  /* ONE EXPORT AT A TIME, and it is not politeness.
     Serialising an SVG, drawing it onto a canvas and encoding a PNG takes long
     enough on a full panel that a reader gets a second press in — and every
     press that lands produces its own file. Twelve presses were twelve
     downloads. The flag is dropped in a `finally`, so a FAILED export re-arms
     the button rather than deadening it. */
  const [exporting, setExporting] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const showingTable = Boolean(forceTable) || (showTable && Boolean(table));

  const run = useCallback(
    async (job?: () => void | Promise<void>) => {
      if (!job || exporting) return;
      setExporting(true);
      try {
        await job();
      } finally {
        setExporting(false);
      }
    },
    [exporting]
  );

  const exportPng = useCallback(
    () => run(() => onExportPng?.(boxRef.current?.querySelector('svg') || null)),
    [run, onExportPng]
  );

  const body = () => {
    if (state.loading && !state.stale) return <ChartSkeleton height={minHeight - 60} />;
    if (state.error) {
      return <ChartError message={state.error} onRetry={state.onRetry} />;
    }
    if (emptyReason) return <ChartEmpty {...emptyReason} />;
    if (showingTable) return <>{table}</>;

    return (
      <Staleable stale={Boolean(state.stale)}>
        <ScrollableChart>
          <ChartArea
            ref={boxRef}
            $aspect={aspect}
            $minHeight={minHeight}
            $minWidth={minWidth}
            role="img"
            aria-label={summary || title}
          >
            <ResponsiveContainer width="100%" height="100%">
              {children as React.ReactElement}
            </ResponsiveContainer>
          </ChartArea>
        </ScrollableChart>
        {/* The same sentence the aria-label carries, in the DOM, so it is
            findable by a test and by a reader using find-on-page. */}
        {summary ? <SrOnly>{summary}</SrOnly> : null}
      </Staleable>
    );
  };

  const canToggle = Boolean(table) && !forceTable && !emptyReason && !state.error;

  return (
    <Panel wide={wide} aria-busy={state.loading || undefined} data-testid={`panel-${id}`}>
      <PanelHead>
        <PanelTitleGroup>
          <PanelTitle>{title}</PanelTitle>
          {caveat ? <PanelCaveat>{caveat}</PanelCaveat> : null}
          {extraCaveat ? <PanelCaveat>{extraCaveat}</PanelCaveat> : null}
        </PanelTitleGroup>
        <PanelControls>
          {/* THE PANEL'S OWN CONTROLS CHANGE THE DRAWING, so they are not
              offered while the drawing is off screen. Left visible they were
              live but inert: pressing Stack over a table moved a highlight and
              changed nothing anybody could see, which is exactly what a broken
              button looks like. The chosen form is remembered and is what
              returns when Chart is pressed. */}
          {showingTable ? null : controls}
          {canToggle ? (
            <PanelToggle
              type="button"
              active={showTable}
              aria-pressed={showTable}
              onClick={() => setShowTable(value => !value)}
            >
              {showTable ? 'Chart' : 'Table'}
            </PanelToggle>
          ) : null}
          {onExportPng || onExportCsv ? (
            <PanelActions>
              {/* NO PNG OVER A TABLE. The picture is what is serialised, and
                  while the table is showing there is no <svg> in this panel at
                  all — so the button used to answer every press with "There is
                  no chart to export here", which reads as a broken button
                  rather than as a control that does not apply. Pressed five
                  times it was five error toasts. CSV stays: the numbers are
                  there in both views, and it is the same file either way. */}
              {onExportPng && !showingTable ? (
                <PanelToggle
                  type="button"
                  onClick={exportPng}
                  disabled={exporting}
                  aria-busy={exporting || undefined}
                  aria-label={`Download ${title} as an image`}
                >
                  PNG
                </PanelToggle>
              ) : null}
              {onExportCsv ? (
                <PanelToggle
                  type="button"
                  onClick={() => run(onExportCsv)}
                  disabled={exporting}
                  aria-busy={exporting || undefined}
                  aria-label={`Download the numbers behind ${title}`}
                >
                  CSV
                </PanelToggle>
              ) : null}
            </PanelActions>
          ) : null}
        </PanelControls>
      </PanelHead>

      <PanelBody>
        <StateSwitch state={bodyState(state, emptyReason)}>{body()}</StateSwitch>
      </PanelBody>

      {footer && !state.error && !emptyReason ? footer : null}

      {notes && notes.length ? (
        <NoteList data-testid="panel-notes">
          {notes.map(note => (
            <NoteItem key={note}>
              <span>{note}</span>
            </NoteItem>
          ))}
        </NoteList>
      ) : null}
    </Panel>
  );
};

/**
 * The same frame for a panel that is NOT a recharts chart — the rhythm heatmap
 * (a CSS grid) and the ranked bar lists (hand-built tracks, because a
 * zero-height recharts bar is not drawn and the zero row is the point).
 *
 * They get the frame and the states; they do not get the ResponsiveContainer.
 */
export const PanelShell: React.FC<Omit<ChartShellProps, 'aspect' | 'minHeight'>> = ({
  id,
  title,
  caveat,
  extraCaveat,
  wide,
  state,
  emptyReason,
  notes,
  controls,
  table,
  forceTable,
  onExportCsv,
  children,
}) => {
  const [showTable, setShowTable] = useState(false);
  const showingTable = Boolean(forceTable) || (showTable && Boolean(table));
  const canToggle = Boolean(table) && !forceTable && !emptyReason && !state.error;

  const body = () => {
    if (state.loading && !state.stale) return <ChartSkeleton />;
    if (state.error) return <ChartError message={state.error} onRetry={state.onRetry} />;
    if (emptyReason) return <ChartEmpty {...emptyReason} />;
    if (showingTable) return <>{table}</>;

    return <Staleable stale={Boolean(state.stale)}>{children}</Staleable>;
  };

  return (
    <Panel wide={wide} aria-busy={state.loading || undefined} data-testid={`panel-${id}`}>
      <PanelHead>
        <PanelTitleGroup>
          <PanelTitle>{title}</PanelTitle>
          {caveat ? <PanelCaveat>{caveat}</PanelCaveat> : null}
          {extraCaveat ? <PanelCaveat>{extraCaveat}</PanelCaveat> : null}
        </PanelTitleGroup>
        <PanelControls>
          {controls}
          {canToggle ? (
            <PanelToggle
              type="button"
              active={showTable}
              aria-pressed={showTable}
              onClick={() => setShowTable(value => !value)}
            >
              {showTable ? 'Chart' : 'Table'}
            </PanelToggle>
          ) : null}
          {onExportCsv ? (
            <PanelActions>
              <PanelToggle
                type="button"
                onClick={onExportCsv}
                aria-label={`Download the numbers behind ${title}`}
              >
                CSV
              </PanelToggle>
            </PanelActions>
          ) : null}
        </PanelControls>
      </PanelHead>

      <PanelBody>
        <StateSwitch state={bodyState(state, emptyReason)}>{body()}</StateSwitch>
      </PanelBody>

      {notes && notes.length ? (
        <NoteList data-testid="panel-notes">
          {notes.map(note => (
            <NoteItem key={note}>
              <span>{note}</span>
            </NoteItem>
          ))}
        </NoteList>
      ) : null}
    </Panel>
  );
};
